// src/hooks/useAuth.ts
import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
// Using our local implementation instead of the package
import { useAuthState } from './useAuthState';
import AuthService from '../services/AuthService';
import { getAppLanguage } from '../utils/language';

/**
 * Custom hook for authentication
 * Provides authentication state and methods
 */
export function useAuth() {
  const [authRefresh, setAuthRefresh] = useState(0);
  const [user, loading, error, isSuperuser] = useAuthState(authRefresh);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    setIsAuthenticated(!!user);
  }, [user]);

  const refreshAuth = () => setAuthRefresh((r) => r + 1);

  /**
   * Register with email and password (backend sets cookie; we refresh auth state)
   */
  const register = async (email: string, password: string) => {
    try {
      const language = getAppLanguage();
      const result = await AuthService.registerWithEmailAndPassword(email, password, language);
      refreshAuth();
      return { success: true, user: result.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Login with email and password
   */
  const login = async (email: string, password: string) => {
    try {
      const language = getAppLanguage();
      const result = await AuthService.signInWithEmailAndPassword(email, password, language);
      refreshAuth();
      return { success: true, user: result.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Login with Google id_token from @react-oauth/google
   */
  const loginWithGoogle = async (idToken: string) => {
    try {
      const result = await AuthService.signInWithGoogle(idToken);
      refreshAuth();
      return { success: true, user: result.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Logout
   */
  const logout = async () => {
    try {
      await AuthService.signOut();
      // Auth state will be updated by useAuthState next tick or manual reload
      setIsAuthenticated(false);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Get user data
   */
  const getUserData = (): User | null => {
    return user || null;
  };

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isSuperuser,
    register,
    login,
    loginWithGoogle,
    logout,
    getUserData
  };
}
